import React, { useState, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Building2, Shield, Zap, Users, Settings, TrendingUp,
  Sparkles, Layers, GitBranch, BookOpen, BarChart3, Globe,
  Target, Send, Workflow, Repeat, LineChart, Database,
  Calculator, Receipt, ShieldCheck, Activity, AlertTriangle,
  Search, UserPlus, Award, Heart, Gift, ClipboardCheck,
  Cog, GitMerge, PieChart, Truck, CheckCircle2, Gauge,
} from 'lucide-react';
import TripleVideoHero from '../components/TripleVideoHero';

import DemoModal from '../components/DemoModal';

const ProductD = () => {
  const [demoOpen, setDemoOpen] = useState(false);
  const capabilitySectionRef = useRef(null);
  const [familyGrowMinHeight, setFamilyGrowMinHeight] = useState(null);

  // "Watch Our Family Grow." must render at the same height as the
  // "Capability Built to Match Your Ambition" section above it. We measure
  // that section's rendered height (which already shares its top/bottom
  // padding) and apply it as a min-height here, keeping both in sync on resize.
  useLayoutEffect(() => {
    const el = capabilitySectionRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const updateHeight = () => setFamilyGrowMinHeight(el.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  // Real GoogLyn AI clients shown in the "Watch Our Family Grow." grid.
  // 13 confirmed clients; remaining 7 of the 20 slots render "Uploading soon".
  const familyGrowClients = [
    { name: 'Being AI', icon: '/family-logos/01-being-ai.png' },
    { name: 'Fromagerie Lumière', icon: '/family-logos/02-fromagerie.png' },
    { name: 'STRYDE', icon: '/family-logos/03-stryde.png' },
    { name: 'Lumière X', icon: '/family-logos/04-lumierex.png' },
    { name: 'Velvet Frost', icon: '/family-logos/05-velvetfrost.png' },
    { name: 'LUMÉ', icon: '/family-logos/06-lume.png' },
    { name: 'Blessed Nacter', icon: '/family-logos/07-blessednacter.png' },
    { name: 'The 6AM Co.', icon: '/family-logos/08-6amco.png' },
    { name: 'VELDONO', icon: '/family-logos/09-veldono.png' },
    { name: 'Vacation Peace', icon: '/family-logos/10-vacationpeace.png' },
    { name: 'Pase', icon: '/family-logos/11-pase.png' },
    { name: 'ARQEN', icon: '/family-logos/12-arqen.png' },
    { name: 'ALTESSA', icon: '/family-logos/13-altessa.png' },
  ];
  const familyGrowSlots = Array.from({ length: 20 }, (_, i) => familyGrowClients[i] || null);

  const features = [
    { icon: Building2, title: 'Digital Transformation', description: 'Modernize without disruption' },
    { icon: TrendingUp, title: 'Enterprise Scale', description: 'Built for large organizations' },
    { icon: Zap, title: 'Zero Chaos', description: 'Smooth implementation process' },
    { icon: Users, title: 'Dedicated Support', description: 'White-glove service' },
    { icon: Settings, title: 'Custom Integration', description: 'Fits your existing infrastructure' },
    { icon: Shield, title: 'ROI-Focused', description: 'Measurable business impact' },
    { icon: ShieldCheck, title: 'Security & Compliance', description: 'Built to SOC 2, GDPR and HIPAA standards' },
    { icon: Layers, title: 'Deployment Flexibility', description: 'Cloud, on-premise, or hybrid' },
  ];

  const videos = [
    'https://customer-assets.emergentagent.com/job_googlyn-ai/artifacts/88nk55qx_hf_20260414_044133_ee12ccc4-6d67-4fb3-85db-97378de7800d.mp4',
    'https://customer-assets.emergentagent.com/job_googlyn-ai/artifacts/bjv4fwiu_hf_20260414_045954_e4e56172-a684-4e1d-9f7c-c430a40d7135.mp4',
    'https://customer-assets.emergentagent.com/job_googlyn-ai/artifacts/bpx75o2t_hf_20260414_050550_25316ea7-5739-43c0-8f69-98e8941d95f7.mp4',
  ];

  return (
    <div className="w-full">
      {/* Section 2: Built for Ambitious Businesses — merged with By Industry / By Function reference lists */}
      <section className="py-16 md:py-24 bg-[#F5F0E8]" data-testid="enterprise-ambitious-combined-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
                Built for the Businesses That Keep the World Running.
              </h2>
              <p className="text-base sm:text-lg text-black/70 max-w-3xl mx-auto">
                AI deployed into the operations that move freight, protect what matters, and keep commerce running — across eight industries, five functions.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              {/* Left: existing By Industry / By Function lists — untouched */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 md:gap-16 max-w-3xl mx-auto" data-testid="enterprise-reference-lists-section">
                <div data-testid="by-industry-list">
                  <h3 className="text-sm md:text-base font-bold text-black/90 mb-4 md:mb-6 uppercase tracking-wider">
                    By Industry
                  </h3>
                  <ul className="space-y-3 md:space-y-4">
                    {[
                      { label: 'Logistics', to: '/solutions/industries/logistics' },
                      { label: 'Insurance', to: '/solutions/industries/insurance' },
                      { label: 'Real Estate', to: '/solutions/industries/real-estate' },
                      { label: 'Retail', to: '/solutions/industries/retail' },
                      { label: 'Healthcare', to: '/solutions/industries/healthcare' },
                      { label: 'Finance', to: '/solutions/industries/finance' },
                      { label: 'Manufacturing', to: '/solutions/industries/manufacturing' },
                      { label: 'Government & Public Safety', to: '/solutions/industries/government' },
                    ].map((item) => (
                      <li key={item.label}>
                        <Link
                          to={item.to}
                          data-testid={`by-industry-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className="inline-block text-sm md:text-base text-black/70 font-light hover:text-[#E8A0B8] transition-all duration-200 ease-out hover:scale-110 origin-left"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div data-testid="by-function-list">
                  <h3 className="text-sm md:text-base font-bold text-black/90 mb-4 md:mb-6 uppercase tracking-wider">
                    By Function
                  </h3>
                  <ul className="space-y-3 md:space-y-4">
                    {[
                      { label: 'Sales', to: '/solutions/sales' },
                      { label: 'Operations', to: '/solutions/operations' },
                      { label: 'Customer Service', to: '/solutions/customer-service' },
                      { label: 'Finance', to: '/solutions/finance' },
                      { label: 'HR & Recruiting', to: '/solutions/human-resources' },
                    ].map((item) => (
                      <li key={item.label}>
                        <Link
                          to={item.to}
                          data-testid={`by-function-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          className="inline-block text-sm md:text-base text-black/70 font-light hover:text-[#E8A0B8] transition-all duration-200 ease-out hover:scale-110 origin-left"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right: Local HTML5 video replacing the previous Cloudflare embed */}
              <div
                className="w-full"
                data-testid="enterprise-industries-media-placeholder"
              >
                <video
                  src="/videos/ES-Main-Animation.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  aria-label="Enterprise industries media video"
                  data-testid="enterprise-industries-media-video"
                  className="w-full rounded-2xl bg-[#E8E4DC]"
                  style={{ aspectRatio: '4 / 3', boxShadow: '0 20px 40px rgba(0,0,0,0.08)', objectFit: 'cover', display: 'block' }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: Content Section 1 */}
      <section ref={capabilitySectionRef} className="py-16 md:py-24 bg-[#F5F7F5]" data-testid="enterprise-capability-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
                Capability Built to Match Your Ambition
              </h2>
              <p className="text-base sm:text-lg text-black/70 max-w-3xl mx-auto">
                Built specifically for large organizations that demand reliability, security, and scale.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, idx) => (
                <Card key={idx} className="h-full bg-[#E8E4DC] border-[#F4C2D4]/30 hover:bg-[#FDF7F9] hover:border-[#E8A0B8]/50 hover:-translate-y-1 transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-[#E8A0B8]" />
                    </div>
                    <CardTitle className="text-black/90 text-lg font-sans">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-black/70 text-sm">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section - Static Logo Grid */}
      <section
        className="py-16 md:py-24 bg-[#E3E8E1] overflow-hidden flex items-center justify-center"
        style={familyGrowMinHeight ? { minHeight: `${familyGrowMinHeight}px` } : undefined}
        data-testid="enterprise-family-grow-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-3xl md:text-4xl font-medium text-black/90" data-testid="enterprise-testimonials-heading">
              Watch Our Family Grow.
            </h2>
            <p className="mt-3 md:mt-4 text-base md:text-lg text-black/60" data-testid="enterprise-testimonials-subtext">
              The businesses that trust GoogLyn AI to run smarter, every day.
            </p>
          </div>
          {/* Framed 5x4 grid (1240px, centred). Below 768px becomes 2 columns.
              Full-width lines above/below, 1px edge lines left/right, 3px dots
              at the four corners of the 1240px frame. */}
          <div className="relative w-full" data-testid="enterprise-testimonials-grid">
            <div className="family-grow-frame">
              {/* Full-width top/bottom lines (span viewport) */}
              <div className="family-grow-hline" style={{ top: 0, left: '50%', right: 'auto', width: '100vw', transform: 'translateX(-50%)' }} />
              <div className="family-grow-hline" style={{ bottom: 0, left: '50%', right: 'auto', width: '100vw', transform: 'translateX(-50%)' }} />
              {/* Left/right edge lines of the 1240px frame */}
              <div className="family-grow-vline" style={{ left: 0 }} />
              <div className="family-grow-vline" style={{ right: 0 }} />
              {/* Corner dots at the four intersections */}
              <span className="family-grow-dot" style={{ left: 0, top: 0, transform: 'translate(-50%, -50%)' }} />
              <span className="family-grow-dot" style={{ right: 0, top: 0, transform: 'translate(50%, -50%)' }} />
              <span className="family-grow-dot" style={{ left: 0, bottom: 0, transform: 'translate(-50%, 50%)' }} />
              <span className="family-grow-dot" style={{ right: 0, bottom: 0, transform: 'translate(50%, 50%)' }} />

              <div className="family-grow-grid">
                {familyGrowSlots.map((slot, idx) => (
                  <div
                    key={`family-grow-${idx}`}
                    className="family-grow-slot"
                    data-testid={`enterprise-testimonial-logo-${idx}`}
                  >
                    {slot ? (
                      <>
                        <img
                          src={slot.icon}
                          alt={`${slot.name} logo`}
                          className="family-grow-slot-icon"
                          draggable="false"
                        />
                        <span className="family-grow-slot-name">{slot.name}</span>
                      </>
                    ) : (
                      <span className="family-grow-slot-empty">Uploading soon</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Triple Video Hero Section - moved to between testimonials and CTA */}
      <TripleVideoHero
        title="Enterprise Solutions"
        description="Transform. Scale. No Chaos. Enterprise-grade AI solutions designed for seamless transformation."
        videos={videos}
      />

      {/* Section 4: CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
              Ready to transform your enterprise?
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-black/70 mb-6 md:mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our enterprise solutions team.
            </p>
            <Button
              size="lg"
              onClick={() => setDemoOpen(true)}
              className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105 text-base px-8"
            >
              Contact the Founders
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default ProductD;